import React, { useEffect, useState } from "react";

// Formik
import * as Yup from "yup";
import { useFormik } from "formik";

import { Loader } from "../../Common/Loader";
import { X, Loader as LoaderIcon, Upload, FileText, Trash2 } from "lucide-react";
import { Common } from "helpers/common";
import { ToastContainer } from "react-toastify";
import Drawer from "Common/Components/Drawer";
import DeleteModal from "Common/DeleteModal";
import { addHomework, updateHomework, getClassList, getTeacherList, uploadHomeworkFile, deleteHomeworkFile } from "helpers/backend_helper";
import { useAuth } from "hooks/useStore";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_green.css";
import Select from "react-select";
import { useProgramStore } from "store/useProgramStore";
import { useClassStore } from "store/useClassStore";

type Props = { onHide: () => void; show: boolean, data: any; onSave: (result: any) => void, onFileDelete: (result: any) => void };
export const AddHomework: React.FC<Props> = ({ onHide, show, data, onSave, onFileDelete }) => {
  const  clientId  = 1;
  const [homeworkData, setHomeworkData] = useState<any>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [teachers, setTeachers] = useState<any[]>([]);
  const [teacherOptions, setTeacherOptions] = useState<any[]>([]);
  const [uploadedFile, setUploadedFile] = useState<any>(null);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [deleteModal, setDeleteModal] = useState<boolean>(false);

  // get REACT_APP_DOCUMENT_URL from env
  const baseDocumentUrl = process.env.REACT_APP_DOCUMENT_URL;
  const { user } = useAuth();
  const { selectedProgram, programs } = useProgramStore();
  const { selectedClass, classes } = useClassStore();

  const uiHelper = new Common();

  const statusOptions = [
    { value: 1, label: "Active" },
    { value: 0, label: "Inactive" }
  ];

  // Program options from store
  const programOptions = programs.map((program: any) => ({
    value: program.programId,
    label: program.programName
  }));

  // Class options from store
  const classOptions = classes.map((classItem: any) => ({
    value: classItem.classId,
    label: classItem.className
  }));

  useEffect(() => {
    if (show) {
      loadTeachers();
      if (data && data.homeworkId) {
        setIsEdit(true);
        setHomeworkData(data);
        // If there's an attachmentUrl, set it as uploadedFile
        if (data.attachmentUrl) {
          setUploadedFile({
            fileName: data.attachmentUrl,
            originalFileName: data.attachmentUrl.split('/').pop() || data.attachmentUrl,
          });
        } else {
          setUploadedFile(null);
        }
        console.log("Edit mode - Homework data:", data);
      } else {
        setIsEdit(false);
        setHomeworkData({});
        setUploadedFile(null);
        console.log("Add mode - New homework");
      }
    }
  }, [show, data]);

  const loadTeachers = async () => {
    try {
      const response = await getTeacherList();
      const teacherData = response.data || [];
      setTeachers(teacherData);
      
      const options = teacherData.map((teacher: any) => ({
        value: teacher.teacherId,
        label: `${teacher.firstName} ${teacher.lastName}`
      }));
      setTeacherOptions(options);
    } catch (error) {
      console.error("Error loading teachers:", error);
      setTeachers([]);
      setTeacherOptions([]);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await uploadHomeworkFile(clientId, formData);
      
      console.log("Upload response:", response);
      
      // Save the uploaded file info - response.data contains the actual data
      const responseData = response.data || response;
      const fileData = {
        fileName: responseData.fileName,
        originalFileName: responseData.originalFileName || file.name,
        fileUrl: responseData.fileUrl,
        fileSize: responseData.fileSize,
        uploadedAt: responseData.uploadedAt,
      };
      
      setUploadedFile(fileData);
      
      // Update formik value
      validation.setFieldValue("attachmentUrl", fileData.fileName);
      
      //uiHelper.showSuccessMessage("File uploaded successfully");
    } catch (error: any) {
      console.error("Error uploading file:", error);
      uiHelper.showErrorMessage(error?.response?.data?.message || "Error uploading file");
    } finally {
      setIsUploading(false);
      // Reset file input
      event.target.value = '';
    }
  };

  const deleteToggle = () => setDeleteModal(!deleteModal);

  const onClickDeleteFile = () => {
    if (!uploadedFile) return;
    setDeleteModal(true);
  };

  const handleFileDelete = async () => {
    if (!uploadedFile) return;

    try {
      const clientId = 1; // Following the pattern from FeesList.tsx
      await deleteHomeworkFile(clientId, homeworkData.homeworkId, uploadedFile.fileName);
      
      setDeleteModal(false);
      setUploadedFile(null);
      validation.setFieldValue("attachmentUrl", "");
      onFileDelete({fileName: uploadedFile.fileName, homeworkId: homeworkData.homeworkId});
      uiHelper.showSuccessMessage("File deleted successfully");
    } catch (error: any) {
      console.error("Error deleting file:", error);
      uiHelper.showErrorMessage(error?.response?.data?.message || "Error deleting file");
      setDeleteModal(false);
    }
  };

  const handleViewFile = () => {
    if (validation.values.attachmentUrl) {
      window.open(baseDocumentUrl + "/homework/" + clientId + "/" + validation.values.attachmentUrl, '_blank');
    }
  };

  const validation: any = useFormik({
    enableReinitialize: true,
    initialValues: {
      homeworkId: (homeworkData && homeworkData.homeworkId) || 0,
      teacherId: (homeworkData && homeworkData.teacherId) || "",
      programId: (homeworkData && homeworkData.programId) || (selectedProgram?.programId || ""),
      classId: (homeworkData && homeworkData.classId) || (selectedClass?.classId || ""),
      title: (homeworkData && homeworkData.title) || "",
      description: (homeworkData && homeworkData.description) || "",
      assignedDate: (homeworkData && homeworkData.assignedDate) || "",
      dueDate: (homeworkData && homeworkData.dueDate) || "",
      totalMarks: (homeworkData && homeworkData.totalMarks) || 0,
      attachmentUrl: (homeworkData && homeworkData.attachmentUrl) || "",
      isActive: (homeworkData && homeworkData.isActive !== undefined) ? homeworkData.isActive : 1,
    },
    validationSchema: Yup.object({
      teacherId: Yup.number().required("Please select a teacher"),
      programId: Yup.number().required("Please select a program"),
      classId: Yup.number().required("Please select a class"),
      title: Yup.string().required("Title is required").max(200, "Title must be at most 200 characters"),
      description: Yup.string().nullable(),
      assignedDate: Yup.date().required("Assigned date is required"),
      dueDate: Yup.date().required("Due date is required")
        .test('is-after-assigned', 'Due date must be after assigned date', function(value) {
          const { assignedDate } = this.parent;
          if (!value || !assignedDate) return true;
          return new Date(value) >= new Date(assignedDate);
        }),
      totalMarks: Yup.number().min(0, "Total marks must be 0 or greater").required("Total marks is required"),
      attachmentUrl: Yup.string().max(300, "Attachment URL must be at most 300 characters").nullable(),
      isActive: Yup.number().required("Status is required"),
    }),

    onSubmit: async (values: any) => {
      console.log("Form submitted with values:", values);
      setIsSaving(true);
      
      try {
        const payload = {
          homeworkId: values.homeworkId || 0,
          teacherId: parseInt(values.teacherId),
          programId: parseInt(values.programId),
          classId: parseInt(values.classId),
          title: values.title,
          description: values.description || null,
          assignedDate: values.assignedDate ? new Date(values.assignedDate).toISOString() : null,
          dueDate: values.dueDate ? new Date(values.dueDate).toISOString() : null,
          totalMarks: parseInt(values.totalMarks) || 0,
          attachmentUrl: values.attachmentUrl || null,
          isActive: parseInt(values.isActive),
          createdBy: user?.userId,
          updatedBy: user?.userId
        };

        console.log("Payload to send:", payload);

        let response;
        if (isEdit && payload.homeworkId > 0) {
          response = await updateHomework(payload);
          uiHelper.showSuccessMessage("Homework updated successfully");
          onSave(payload);
        } else {
          response = await addHomework(payload);
          onSave(response);
          uiHelper.showSuccessMessage("Homework added successfully");
        }

        console.log("Response:", response);
        validation.resetForm();
      } catch (error: any) {
        console.error("Error saving homework:", error);
        uiHelper.showErrorMessage(error?.response?.data?.message || "Error saving homework");
      } finally {
        setIsSaving(false);
      }
    },
  });

  return (
    <React.Fragment>
    <Drawer 
      show={show} 
      onHide={onHide} 
      id="homeworkDrawer" 
      drawer-end="true" 
      className="fixed inset-y-0 flex flex-col transition-transform duration-600 ease-in-out transform bg-white shadow dark:bg-zink-600 ltr:right-0 rtl:left-0 w-5/6 flex z-drawer"
    >
      <ToastContainer closeButton={false} limit={1} />
      
      <div className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-zink-500">
        <div className="grow">
          <h5 className="mb-0 text-16">{isEdit ? "Edit Homework" : "Add Homework"}</h5>
        </div>
        <div className="shrink-0">
          <Drawer.Header data-drawer-close="homeworkDrawer" className="transition-all duration-150 ease-linear text-slate-500 hover:text-slate-800 dark:text-zink-200 dark:hover:text-zink-50">
            <X className="size-4"></X>
          </Drawer.Header>
        </div>
      </div>

      <Drawer.Body className="h-full p-0 overflow-y-auto">
        {isLoading ? (
          <Loader />
        ) : (
          <form
            id="homeworkForm"
            className="create-form h-full"
            onSubmit={(e) => {
              e.preventDefault();
              validation.handleSubmit();
              return false;
            }}
          >
            <div className="p-4">
              <div className="space-y-6">
                
                {/* Basic Information Section */}
                <div className="bg-slate-50 dark:bg-zink-600 p-4 rounded-lg">
                  <div className="mb-4">
                    <h6 className="text-15 font-semibold text-slate-700 dark:text-zink-100 bg-white dark:bg-zink-700 px-3 py-2 rounded-md shadow-sm border border-slate-200 dark:border-zink-500">
                      📋 Basic Information
                    </h6>
                  </div>
                  
                  <div className="grid grid-cols-1 gap-4">
                    {/* Teacher, Program, Class on same line */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {/* Teacher Select */}
                      <div>
                        <label htmlFor="teacherId" className="inline-block mb-2 text-base font-medium">
                          Teacher <span className="text-red-500">*</span>
                        </label>
                        <Select
                          id="teacherId"
                          name="teacherId"
                          options={teacherOptions}
                          value={teacherOptions.find(option => option.value === validation.values.teacherId) || null}
                          onChange={(option: any) => {
                            validation.setFieldValue("teacherId", option?.value || "");
                          }}
                          onBlur={() => validation.setFieldTouched("teacherId", true)}
                          placeholder="Select Teacher"
                          className="basic-single"
                          classNamePrefix="select"
                          theme={(theme) => ({
                            ...theme,
                            colors: {
                              ...theme.colors,
                              primary: '#3b71ca',
                            },
                          })}
                        />
                        {validation.touched.teacherId && validation.errors.teacherId ? (
                          <p className="text-red-500 text-sm mt-1">{validation.errors.teacherId}</p>
                        ) : null}
                      </div>

                      {/* Program Select */}
                      <div>
                        <label htmlFor="programId" className="inline-block mb-2 text-base font-medium">
                          Program <span className="text-red-500">*</span>
                        </label>
                        <Select
                          id="programId"
                          name="programId"
                          options={programOptions}
                          value={programOptions.find(option => option.value === validation.values.programId) || null}
                          onChange={(option: any) => {
                            validation.setFieldValue("programId", option?.value || "");
                          }}
                          onBlur={() => validation.setFieldTouched("programId", true)}
                          placeholder="Select Program"
                          className="basic-single"
                          classNamePrefix="select"
                          theme={(theme) => ({
                            ...theme,
                            colors: {
                              ...theme.colors,
                              primary: '#3b71ca',
                            },
                          })}
                        />
                        {validation.touched.programId && validation.errors.programId ? (
                          <p className="text-red-500 text-sm mt-1">{validation.errors.programId}</p>
                        ) : null}
                      </div>

                      {/* Class Select */}
                      <div>
                        <label htmlFor="classId" className="inline-block mb-2 text-base font-medium">
                          Class <span className="text-red-500">*</span>
                        </label>
                        <Select
                          id="classId"
                          name="classId"
                          options={classOptions}
                          value={classOptions.find(option => option.value === validation.values.classId) || null}
                          onChange={(option: any) => {
                            validation.setFieldValue("classId", option?.value || "");
                          }}
                          onBlur={() => validation.setFieldTouched("classId", true)}
                          placeholder="Select Class"
                          className="basic-single"
                          classNamePrefix="select"
                          theme={(theme) => ({
                            ...theme,
                            colors: {
                              ...theme.colors,
                              primary: '#3b71ca',
                            },
                          })}
                        />
                        {validation.touched.classId && validation.errors.classId ? (
                          <p className="text-red-500 text-sm mt-1">{validation.errors.classId}</p>
                        ) : null}
                      </div>
                    </div>

                    {/* Title - Full width */}
                    <div>
                      <label htmlFor="title" className="inline-block mb-2 text-base font-medium">
                        Title <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="title"
                        name="title"
                        className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                        placeholder="Enter homework title"
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        value={validation.values.title || ""}
                      />
                      {validation.touched.title && validation.errors.title ? (
                        <p className="text-red-500 text-sm mt-1">{validation.errors.title}</p>
                      ) : null}
                    </div>

                    {/* Description - Full width */}
                    <div>
                      <label htmlFor="description" className="inline-block mb-2 text-base font-medium">
                        Description
                      </label>
                      <textarea
                        id="description"
                        name="description"
                        rows={4}
                        className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                        placeholder="Enter homework description"
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        value={validation.values.description || ""}
                      />
                      {validation.touched.description && validation.errors.description ? (
                        <p className="text-red-500 text-sm mt-1">{validation.errors.description}</p>
                      ) : null}
                    </div>
                  </div>
                </div>

                {/* Dates & Details Section */}
                <div className="bg-slate-50 dark:bg-zink-600 p-4 rounded-lg">
                  <div className="mb-4">
                    <h6 className="text-15 font-semibold text-slate-700 dark:text-zink-100 bg-white dark:bg-zink-700 px-3 py-2 rounded-md shadow-sm border border-slate-200 dark:border-zink-500">
                      📅 Dates & Details
                    </h6>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Assigned Date */}
                    <div>
                      <label htmlFor="assignedDate" className="inline-block mb-2 text-base font-medium">
                        Assigned Date <span className="text-red-500">*</span>
                      </label>
                      <Flatpickr
                        id="assignedDate"
                        name="assignedDate"
                        className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                        placeholder="Select date"
                        options={{
                          dateFormat: "Y-m-d",
                        }}
                        onChange={(date: any) => {
                          validation.setFieldValue("assignedDate", date[0] ? date[0].toISOString().split('T')[0] : "");
                        }}
                        value={validation.values.assignedDate || ""}
                      />
                      {validation.touched.assignedDate && validation.errors.assignedDate ? (
                        <p className="text-red-500 text-sm mt-1">{validation.errors.assignedDate}</p>
                      ) : null}
                    </div>

                    {/* Due Date */}
                    <div>
                      <label htmlFor="dueDate" className="inline-block mb-2 text-base font-medium">
                        Due Date <span className="text-red-500">*</span>
                      </label>
                      <Flatpickr
                        id="dueDate"
                        name="dueDate"
                        className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                        placeholder="Select date"
                        options={{
                          dateFormat: "Y-m-d",
                          minDate: validation.values.assignedDate || undefined,
                        }}
                        onChange={(date: any) => {
                          validation.setFieldValue("dueDate", date[0] ? date[0].toISOString().split('T')[0] : "");
                        }}
                        value={validation.values.dueDate || ""}
                      />
                      {validation.touched.dueDate && validation.errors.dueDate ? (
                        <p className="text-red-500 text-sm mt-1">{validation.errors.dueDate}</p>
                      ) : null}
                    </div>

                    {/* Total Marks */}
                    <div>
                      <label htmlFor="totalMarks" className="inline-block mb-2 text-base font-medium">
                        Total Marks <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        id="totalMarks"
                        name="totalMarks"
                        min="0"
                        className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                        placeholder="Enter total marks"
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        value={validation.values.totalMarks || ""}
                      />
                      {validation.touched.totalMarks && validation.errors.totalMarks ? (
                        <p className="text-red-500 text-sm mt-1">{validation.errors.totalMarks}</p>
                      ) : null}
                    </div>

                    {/* Status */}
                    <div>
                      <label className="inline-block mb-2 text-base font-medium">
                        Status <span className="text-red-500">*</span>
                      </label>
                      <div className="flex gap-4 mt-2">
                        <div className="flex items-center">
                          <input
                            type="radio"
                            id="statusActive"
                            name="isActive"
                            value="1"
                            checked={validation.values.isActive === 1}
                            onChange={() => validation.setFieldValue("isActive", 1)}
                            onBlur={validation.handleBlur}
                            className="w-4 h-4 text-custom-500 bg-white border-slate-200 focus:ring-custom-500 dark:bg-zink-700 dark:border-zink-500"
                          />
                          <label htmlFor="statusActive" className="ml-2 text-sm font-medium text-slate-700 dark:text-zink-100 cursor-pointer">
                            Active
                          </label>
                        </div>
                        <div className="flex items-center">
                          <input
                            type="radio"
                            id="statusInactive"
                            name="isActive"
                            value="0"
                            checked={validation.values.isActive === 0}
                            onChange={() => validation.setFieldValue("isActive", 0)}
                            onBlur={validation.handleBlur}
                            className="w-4 h-4 text-custom-500 bg-white border-slate-200 focus:ring-custom-500 dark:bg-zink-700 dark:border-zink-500"
                          />
                          <label htmlFor="statusInactive" className="ml-2 text-sm font-medium text-slate-700 dark:text-zink-100 cursor-pointer">
                            Inactive
                          </label>
                        </div>
                      </div>
                      {validation.touched.isActive && validation.errors.isActive ? (
                        <p className="text-red-500 text-sm mt-1">{validation.errors.isActive}</p>
                      ) : null}
                    </div>

                    {/* File Attachment - Full width */}
                    <div className="md:col-span-2">
                      <label htmlFor="attachmentFile" className="inline-block mb-2 text-base font-medium">
                        Attachment File
                      </label>
                      
                      {!uploadedFile ? (
                        <div className="flex items-center gap-2">
                          <input
                            type="file"
                            id="attachmentFile"
                            name="attachmentFile"
                            className="hidden"
                            onChange={handleFileUpload}
                            disabled={isUploading}
                          />
                          <label
                            htmlFor="attachmentFile"
                            className={`inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-custom-500 border border-transparent rounded-md hover:bg-custom-600 focus:outline-none focus:ring-2 focus:ring-custom-500 cursor-pointer ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}`}
                          >
                            {isUploading ? (
                              <>
                                <LoaderIcon className="inline-block size-4 mr-2 animate-spin" />
                                Uploading...
                              </>
                            ) : (
                              <>
                                <Upload className="size-4 mr-2" />
                                Choose File
                              </>
                            )}
                          </label>
                          <span className="text-sm text-slate-500 dark:text-zink-300">
                            {isUploading ? 'Please wait...' : 'Select a file to upload'}
                          </span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-zink-700 border border-slate-200 dark:border-zink-500 rounded-md">
                          <div className="flex items-center justify-center size-10 bg-custom-100 dark:bg-custom-500/20 rounded-md">
                            <FileText className="size-5 text-custom-500" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p 
                              className="text-sm font-medium text-slate-700 dark:text-zink-100 truncate cursor-pointer hover:text-custom-500 hover:underline transition-colors"
                              onClick={handleViewFile}
                              title="Click to view file"
                            >
                              {uploadedFile.originalFileName}
                            </p>
                            {uploadedFile.fileSize && (
                              <p className="text-xs text-slate-500 dark:text-zink-300">
                                {(uploadedFile.fileSize / 1024).toFixed(2)} KB
                              </p>
                            )}
                          </div>
                          <div className="flex gap-2">
                            { validation.values.attachmentUrl && (
                              <button
                                type="button"
                                className="inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-blue-500 border border-transparent rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                onClick={handleViewFile}
                                title="View File"
                              >
                                <FileText className="size-4" />
                              </button>
                            )}
                            <button
                              type="button"
                              className="inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-red-500 border border-transparent rounded-md hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500"
                              onClick={onClickDeleteFile}
                              title="Delete File"
                            >
                              <Trash2 className="size-4" />
                            </button>
                          </div>
                        </div>
                      )}
                      
                      {validation.touched.attachmentUrl && validation.errors.attachmentUrl ? (
                        <p className="text-red-500 text-sm mt-1">{validation.errors.attachmentUrl}</p>
                      ) : null}
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </form>
        )}
      </Drawer.Body>

      <Drawer.Footer className="flex items-center justify-end p-4 border-t border-slate-200 dark:border-zink-500 bg-white dark:bg-zink-600">
        <div className="flex gap-2">
          <button
            type="button"
            className="text-slate-500 btn bg-white border-slate-200 hover:text-slate-600 hover:bg-slate-100 hover:border-slate-200 focus:text-slate-600 focus:bg-slate-100 focus:border-slate-200 focus:ring focus:ring-slate-100 active:text-slate-600 active:bg-slate-100 active:border-slate-200 active:ring active:ring-slate-100 dark:bg-zink-700 dark:hover:bg-zink-600 dark:border-zink-500 dark:text-zink-200"
            onClick={onHide}
            disabled={isSaving}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="homeworkForm"
            className="text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20"
            disabled={isSaving}
            onClick={() => validation.handleSubmit()}
          >
            {isSaving ? (
              <>
                <LoaderIcon className="inline-block size-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              isEdit ? "Update Homework" : "Add Homework"
            )}
          </button>
        </div>
      </Drawer.Footer>
    </Drawer>

    {/* Delete File Modal */}
    <DeleteModal
      show={deleteModal}
      onHide={deleteToggle}
      onDelete={handleFileDelete}
      message="Are you certain you want to delete this file?"
      buttonCaption="Yes, Delete It!"
    />
    </React.Fragment>
  );
};

